import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EmptyState, FreshnessIndicator, StatusBadge, type FreshnessState } from '@pazportal/ui';
import { supabase } from '../lib/supabase';
import { usePlatformMetrics } from '../hooks/usePlatformMetrics';
import { formatDateTime, timeAgo } from '../lib/formatters';

type NetworkAlert = {
  id: string;
  severity: string | null;
  status: string | null;
  alert_name: string | null;
  message: string | null;
  detected_on: string | null;
  device_id: string | null;
  location_id: string | null;
  customer_id: string | null;
  devices: { name: string | null } | null;
  locations: { name: string | null } | null;
  customers: { name: string | null } | null;
  entity_name: string | null;
};

const ALERT_COLUMNS =
  'id, severity, status, alert_name, message, detected_on, device_id, location_id, customer_id, entity_name, devices(name), locations(name), customers(name)';

type RealtimeStatus = 'connecting' | 'subscribed' | 'error' | 'closed';

function isOpenAlert(status: string | null | undefined): boolean {
  if (!status) return true;
  const s = status.toLowerCase();
  return s !== 'cleared' && s !== 'resolved';
}

function deviceLabel(alert: NetworkAlert): string {
  return alert.devices?.name ?? alert.entity_name ?? '—';
}

function locationLabel(alert: NetworkAlert): string {
  return alert.locations?.name ?? '—';
}

function customerLabel(alert: NetworkAlert): string {
  return alert.customers?.name ?? '—';
}

export function CommandCenterPage() {
  const navigate = useNavigate();
  const { data: metrics } = usePlatformMetrics();
  const [alerts, setAlerts] = useState<NetworkAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lastRefreshAt, setLastRefreshAt] = useState<string | null>(null);
  const [rtStatus, setRtStatus] = useState<RealtimeStatus>('connecting');

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      const { data, error } = await supabase
        .from('network_alerts')
        .select(ALERT_COLUMNS)
        .or('status.is.null,and(status.neq.cleared,status.neq.resolved)')
        .order('detected_on', { ascending: false })
        .limit(50);

      if (error) throw error;
      if (isMounted) {
        setAlerts((data as NetworkAlert[] | null) ?? []);
        setLoadError(null);
        setLoading(false);
        setLastRefreshAt(new Date().toISOString());
      }
    };

    load().catch((e: unknown) => {
      if (isMounted) {
        setLoading(false);
        setLoadError(e instanceof Error ? e.message : 'Could not load alerts.');
      }
    });

    const refreshRow = async (id: string) => {
      setLastRefreshAt(new Date().toISOString());
      const { data } = await supabase.from('network_alerts').select(ALERT_COLUMNS).eq('id', id).maybeSingle();
      const row = data as NetworkAlert | null;
      if (!row || !isOpenAlert(row.status)) {
        setAlerts((prev) => prev.filter((a) => a.id !== id));
        return;
      }
      setAlerts((prev) => [row, ...prev.filter((a) => a.id !== row.id)].slice(0, 50));
    };

    const channel = supabase
      .channel('network_alerts')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'network_alerts' },
        (payload) => {
          const id = (payload.new as { id?: string } | null)?.id;
          if (id) void refreshRow(id);
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'network_alerts' },
        (payload) => {
          const id = (payload.new as { id?: string } | null)?.id;
          if (id) void refreshRow(id);
        },
      )
      .subscribe((status) => {
        if (!isMounted) return;
        // Supabase Realtime emits: SUBSCRIBED | CHANNEL_ERROR | TIMED_OUT | CLOSED
        if (status === 'SUBSCRIBED') setRtStatus('subscribed');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') setRtStatus('error');
        else if (status === 'CLOSED') setRtStatus('closed');
      });

    return () => {
      isMounted = false;
      supabase.removeChannel(channel);
    };
  }, []);

  const activeCount = useMemo(
    () => alerts.filter((a) => isOpenAlert(a.status)).length,
    [alerts],
  );

  // Surface freshness explicitly. If Realtime is not subscribed we cannot
  // guarantee live data, so the operator sees "offline / last known good".
  const forcedFreshness: FreshnessState | undefined =
    rtStatus === 'subscribed' ? undefined
    : rtStatus === 'connecting' ? 'unknown'
    : 'offline';

  const auvikConnected = metrics?.auvik.connected ?? null;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-surface p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-text">Command Center</h1>
            <p className="text-sm text-text-muted mt-1">
              Open Auvik alerts across every customer, updated by Realtime as the alert ingest writes them.
            </p>
            <p className="text-xs text-text-faint mt-2">
              {auvikConnected === false
                ? 'Auvik not connected: no tenant is mapped and no device has been received, so no alert can arrive.'
                : `Active alerts: ${activeCount}`}
            </p>
          </div>
          <FreshnessIndicator
            lastUpdatedAt={lastRefreshAt}
            forcedState={forcedFreshness}
            streaming={rtStatus === 'subscribed'}
            label="Alerts"
            expectedRefreshMs={2 * 60 * 1000}
            staleMs={10 * 60 * 1000}
          />
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-border bg-surface px-4 py-10 text-center text-text-faint" role="status">Loading alerts…</div>
      ) : loadError ? (
        <div role="alert" className="rounded-lg border border-border bg-surface px-4 py-10 text-center text-notification break-words">
          Could not load alerts: {loadError}. Refresh the page to try again.
        </div>
      ) : alerts.length === 0 ? (
        <div className="rounded-lg border border-border bg-surface px-4 py-12">
          <EmptyState
            icon={<svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.8-2.9L13.8 5a2 2 0 00-3.6 0L3.2 16.1A2 2 0 005 19z" /></svg>}
            title={auvikConnected === false ? 'Auvik not connected' : 'No open alerts'}
            subtitle={
              auvikConnected === false
                ? 'Alerts arrive once a valid Auvik API key is configured and tenants are mapped to customers.'
                : 'Open Auvik alerts that are not cleared or resolved appear here.'
            }
            action={auvikConnected === false ? { label: 'Open Integrations', onClick: () => navigate('/integrations') } : undefined}
          />
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {alerts.map((alert) => {
              const href = alert.customer_id ? `/customers/${alert.customer_id}` : null;
              const body = (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-text break-words">{alert.alert_name ?? 'Unnamed alert'}</p>
                    {alert.severity ? <StatusBadge status={alert.severity} /> : null}
                  </div>
                  {alert.message && <p className="text-xs text-text-muted mt-1 break-words line-clamp-3">{alert.message}</p>}
                  <p className="text-xs text-text-muted mt-1.5 break-words">
                    {customerLabel(alert)} · {deviceLabel(alert)} / {locationLabel(alert)}
                  </p>
                  <p className="text-xs text-text-faint mt-1">
                    {alert.detected_on ? `${formatDateTime(alert.detected_on)} (${timeAgo(alert.detected_on)})` : 'Detection time unknown'}
                  </p>
                </>
              );
              return href ? (
                <Link key={alert.id} to={href} className="block rounded-lg border border-border bg-surface p-3 min-h-[44px] active:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                  {body}
                </Link>
              ) : (
                <div key={alert.id} className="rounded-lg border border-border bg-surface p-3">{body}</div>
              );
            })}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block rounded-lg border border-border bg-surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-text-muted border-b border-border">
                  <th className="px-4 py-3 font-medium">Severity</th>
                  <th className="px-4 py-3 font-medium">Alert</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Device / Location</th>
                  <th className="px-4 py-3 font-medium">Detected</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {alerts.map((alert) => {
                  const href = alert.customer_id ? `/customers/${alert.customer_id}` : null;
                  return (
                    <tr
                      key={alert.id}
                      className={`text-text ${href ? 'hover:bg-surface-strong cursor-pointer' : ''}`}
                      onClick={() => { if (href) navigate(href); }}
                    >
                      <td className="px-4 py-3">
                        {alert.severity ? <StatusBadge status={alert.severity} /> : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium">{alert.alert_name ?? 'Unnamed alert'}</p>
                        {alert.message && (
                          <p className="text-xs text-text-muted mt-0.5 line-clamp-2 break-words">{alert.message}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">{customerLabel(alert)}</td>
                      <td className="px-4 py-3 text-xs">
                        {deviceLabel(alert)} / {locationLabel(alert)}
                      </td>
                      <td className="px-4 py-3 text-xs text-text-muted whitespace-nowrap">
                        {alert.detected_on ? `${formatDateTime(alert.detected_on)} (${timeAgo(alert.detected_on)})` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
